import { z } from "zod";

import { OpaqueIdSchema } from "./identity-schemas";
import { WorkspaceMemberRoleSchema } from "./workspace-user-schemas";

/**
 * Contracts for administering a directory provisioning connection (ADR-0070).
 *
 * These describe the *portal* surface — what an organization administrator configures. The SCIM
 * protocol resources an identity provider exchanges are a separate contract, because the two have
 * different audiences and must be free to diverge: this one may be reshaped for the panel, and that
 * one is pinned by RFC 7643.
 */

/**
 * Setup guidance and field defaults only. This must never change protocol behaviour: the request
 * path is identical for every value, exactly as `SamlProviderHintSchema` is for sign-on.
 */
export const ScimProviderHintSchema = z.enum(["entra", "okta", "generic"]);

/**
 * `draft` is configured but refuses every provisioning request. `enabled` accepts them. `disabled`
 * refuses them again — note it does not undo anything already provisioned, because unwinding a
 * customer's directory on a misclick would be far worse than leaving accounts in place. Cutting a
 * person's access is still membership suspension.
 */
export const ScimConnectionStatusSchema = z.enum(["draft", "enabled", "disabled"]);

export const ScimConnectionDisplayNameSchema = z
  .string()
  .trim()
  .min(2, "Name this connection so it is recognisable later.")
  .max(120, "Keep the name under 120 characters.");

/**
 * A human label for one credential, so an administrator rotating tokens can tell which is which.
 * It is not a secret and is never derived from one.
 */
export const ScimCredentialLabelSchema = z
  .string()
  .trim()
  .min(1, "Label this credential.")
  .max(80, "Keep the label under 80 characters.");

export const ScimConnectionCreateSchema = z.strictObject({
  displayName: ScimConnectionDisplayNameSchema,
  providerHint: ScimProviderHintSchema,
  /**
   * Optionally pair this with the workspace's SAML connection. Null is a first-class answer:
   * provisioning without single sign-on is a legitimate configuration, and so is the reverse.
   */
  samlConnectionId: OpaqueIdSchema.nullable(),
});

export const ScimConnectionUpdateSchema = ScimConnectionCreateSchema;

export const ScimConnectionStatusUpdateSchema = z.strictObject({
  status: ScimConnectionStatusSchema,
});

export const ScimCredentialCreateSchema = z.strictObject({
  label: ScimCredentialLabelSchema,
});

/**
 * A credential as the portal may display it.
 *
 * There is deliberately no field here that could carry any part of the secret — not a prefix, not a
 * suffix, not a masked form. The plaintext is returned exactly once, by the creation endpoint, and
 * is never readable again.
 */
export const ScimCredentialViewSchema = z.strictObject({
  id: OpaqueIdSchema,
  label: ScimCredentialLabelSchema,
  status: z.enum(["active", "revoked"]),
  createdAt: z.number().int().positive(),
  lastUsedAt: z.number().int().positive().nullable(),
  revokedAt: z.number().int().positive().nullable(),
});

/** The one and only time a token is legible. */
export const ScimCredentialSecretSchema = z.strictObject({
  credential: ScimCredentialViewSchema,
  /**
   * Shown once and never stored in plaintext. The portal must warn before dismissing it, because
   * there is no recovery path — an administrator who loses it mints a replacement.
   */
  token: z.string().min(32).max(256),
});

/**
 * What an administrator pastes into Entra or Okta.
 *
 * Derived at runtime from `PLATFORM_SHELL_ORIGIN` and never stored, so a staging deployment cannot
 * publish a production URL and the string shown here is byte-for-byte the one the server validates.
 */
export const ScimIdentifiersSchema = z.strictObject({
  tenantUrl: z.url(),
});

export const ScimGroupViewSchema = z.strictObject({
  id: OpaqueIdSchema,
  displayName: z.string().min(1).max(256),
  externalId: z.string().min(1).max(256).nullable(),
  memberCount: z.number().int().nonnegative(),
  /** Null until an administrator decides what this group grants. An unmapped group grants nothing. */
  mapping: z
    .strictObject({
      id: OpaqueIdSchema,
      role: WorkspaceMemberRoleSchema,
      toolAccess: z.array(OpaqueIdSchema).max(100),
    })
    .nullable(),
});

export const ScimGroupMappingUpsertSchema = z.strictObject({
  role: WorkspaceMemberRoleSchema,
  toolAccess: z
    .array(OpaqueIdSchema)
    .max(100)
    .refine(
      (toolAccess) => new Set(toolAccess).size === toolAccess.length,
      "Choose each tool only once.",
    ),
});

export const ScimConnectionViewSchema = z.strictObject({
  id: OpaqueIdSchema,
  organizationId: OpaqueIdSchema,
  displayName: ScimConnectionDisplayNameSchema,
  providerHint: ScimProviderHintSchema,
  status: ScimConnectionStatusSchema,
  samlConnectionId: OpaqueIdSchema.nullable(),
  enabledAt: z.number().int().positive().nullable(),
  lastRequestAt: z.number().int().positive().nullable(),
  identifiers: ScimIdentifiersSchema,
  credentials: z.array(ScimCredentialViewSchema).max(20),
  groups: z.array(ScimGroupViewSchema).max(200),
  /** How many accounts this connection currently manages, so disabling it states its own cost. */
  managedUserCount: z.number().int().nonnegative(),
});

export const ScimConnectionResponseSchema = z.strictObject({
  connection: ScimConnectionViewSchema.nullable(),
});

export type ScimIdentifiers = z.infer<typeof ScimIdentifiersSchema>;
export type ScimProviderHint = z.infer<typeof ScimProviderHintSchema>;
export type ScimConnectionStatus = z.infer<typeof ScimConnectionStatusSchema>;
export type ScimConnectionCreate = z.infer<typeof ScimConnectionCreateSchema>;
export type ScimConnectionView = z.infer<typeof ScimConnectionViewSchema>;
export type ScimCredentialView = z.infer<typeof ScimCredentialViewSchema>;
export type ScimGroupView = z.infer<typeof ScimGroupViewSchema>;
export type ScimGroupMappingUpsert = z.infer<typeof ScimGroupMappingUpsertSchema>;
